import { useState, useEffect, useCallback, useMemo } from "react";
import { preferFacingModeForCameraPreview } from "../utils";

function labelOf(d) {
  return (d.label || "").toLowerCase();
}

function isBackLabel(d) {
  const l = labelOf(d);
  return l.includes("back") || l.includes("rear") || l.includes("environment");
}

function isFrontLabel(d) {
  const l = labelOf(d);
  return l.includes("front") || l.includes("user") || l.includes("face") || l.includes("selfie");
}

/**
 * Camera devices enumeration and flip. Exposes device list, selected device, and capability setters for preview.
 * On many phones, front camera fails or device order is wrong; we pair back/front by label when possible, use
 * facingMode when fewer than two deviceIds are exposed, and re-enumerate after the stream starts (see onStreamReady).
 */
export function useCameraDevices() {
  const useFacingOnMobile = useMemo(() => preferFacingModeForCameraPreview(), []);
  const [videoPermission, setVideoPermission] = useState("unknown");
  const [videoDevices, setVideoDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  /** Mobile: back first (environment). Desktop: user when preview uses facingMode only. */
  const [facingMode, setFacingMode] = useState(() =>
    preferFacingModeForCameraPreview() ? "environment" : "user"
  );
  const [flashSupported, setFlashSupported] = useState(false);
  const [zoomRange, setZoomRange] = useState(null);

  const refreshDevices = useCallback(() => {
    if (videoPermission === "denied" || !navigator.mediaDevices?.enumerateDevices) return;
    navigator.mediaDevices
      .enumerateDevices()
      .then((devices) => {
        const videoOnly = devices.filter((d) => d.kind === "videoinput");
        setVideoDevices(videoOnly);
        const withId = videoOnly.filter((d) => d.deviceId && d.deviceId !== "");
        if (withId.length >= 2) {
          const preferred =
            withId.find(isBackLabel)?.deviceId ??
            withId.find(isFrontLabel)?.deviceId ??
            withId[0].deviceId;
          setSelectedDevice((prev) => {
            if (prev && withId.some((d) => d.deviceId === prev)) return prev;
            return preferred;
          });
        } else {
          setSelectedDevice(null);
        }
      })
      .catch(() => setVideoDevices([]));
  }, [videoPermission]);

  useEffect(() => {
    if (!navigator.permissions?.query) {
      setVideoPermission("unknown");
      return;
    }
    navigator.permissions
      .query({ name: "camera" })
      .then((status) => {
        setVideoPermission(status.state);
        status.onchange = () => setVideoPermission(status.state);
      })
      .catch(() => setVideoPermission("unknown"));
  }, []);

  useEffect(() => {
    refreshDevices();
  }, [refreshDevices]);

  const handleFlipCamera = useCallback(() => {
    setFlashSupported(false);
    setZoomRange(null);
    if (useFacingOnMobile) {
      setFacingMode((prev) => (prev === "user" ? "environment" : "user"));
      return;
    }
    const withId = videoDevices.filter((d) => d.deviceId && d.deviceId !== "");
    if (withId.length >= 2) {
      const current = withId.find((d) => d.deviceId === selectedDevice) ?? withId[0];
      const front = withId.find(isFrontLabel);
      const back = withId.find(isBackLabel);
      let nextId;
      if (front && back) {
        const onFront = isFrontLabel(current) || current.deviceId === front.deviceId;
        nextId = onFront ? back.deviceId : front.deviceId;
      } else {
        const idx = Math.max(
          0,
          withId.findIndex((d) => d.deviceId === selectedDevice)
        );
        nextId = withId[(idx + 1) % withId.length].deviceId;
      }
      if (nextId) setSelectedDevice(nextId);
      return;
    }
    setFacingMode((prev) => (prev === "user" ? "environment" : "user"));
  }, [videoDevices, selectedDevice, useFacingOnMobile]);

  return {
    videoDevices,
    selectedDevice,
    facingMode,
    useFacingOnMobile,
    flashSupported,
    setFlashSupported,
    zoomRange,
    setZoomRange,
    handleFlipCamera,
    refreshDevices,
  };
}
